import { useContext } from "react";
import { AppContext } from "../App";
import { 
  IoGridOutline,
  IoHeartOutline,
  IoFlaskOutline,
  IoCartOutline,
  IoMedkitOutline,
  IoShieldCheckmarkOutline,
  IoCellularOutline,
  IoChevronForwardOutline
} from "react-icons/io5";

export default function HomeBanner2() {
  const appContext = useContext(AppContext);

  // Structural mapping for the 7 categorized pillars
  const serviceCategories = [
    {
      id: "clinical",
      icon: <IoGridOutline size={24} />,
      title: "Clinical Services",
      desc: "Comprehensive outpatient care, continuity medicine, and structured therapeutic support.",
      subpillars: [
        "Outpatient & General Consultation (OPD)",
        "Family Care Model",
        "Non-Communicable Disease (NCD) Clinic",
        "Minor Surgical Procedures Unit",
        "Mental Health & Counseling"
      ],
      target: 'clinical'
    },
    {
      id: "maternal",
      icon: <IoHeartOutline size={24} />,
      title: "Maternal, Child & Reproductive Health",
      desc: "Dedicated clinical paths ensuring long-term wellness for mothers, infants, and families.",
      subpillars: [
        "Maternal & Child Health (MCH) Clinic",
        "Immunization Clinic (EPI)",
        "Cervical Cancer Screening Clinic",
        "Sexual & Reproductive Health (SRH)"
      ],
      target: 'maternal'
    },
    {
      id: "diagnostic",
      icon: <IoFlaskOutline size={24} />,
      title: "Diagnostic Services",
      desc: "Advanced processing suites providing rapid, evidence-based results under safety protocols.",
      subpillars: [
        "Advanced Laboratory Services",
        "Radiology & Diagnostic Imaging (TAEC Compliant)",
        "ECG & Basic Cardiac Rhythm Monitoring"
      ],
      target: 'diagnosis'
    },
    {
      id: "pharmacy",
      icon: <IoCartOutline size={24} />,
      title: "Pharmacy Services",
      desc: "One-stop clinical repository operating under qualified leadership for prescription fulfillment.",
      subpillars: [
        "Clinical Pharmacy, Screening & Dispensing",
        "Pharmaceutical Products Supply",
        "Biomedical Equipment Access"
      ],
      target: 'pharmacy'
    },
    {
      id: "specialized",
      icon: <IoMedkitOutline size={24} />,
      title: "Specialized Clinics",
      desc: "Targeted medical interventions focused on specific demographic vulnerabilities and requirements.",
      subpillars: [
        "Care and Treatment Clinic (CTC Program)",
        "Occupational Health Services Clinic",
        "Nutrition & Dietetics Clinic",
        "Adolescent Friendly Health Clinic"
      ],
      target: 'specialized'
    },
    {
      id: "wellness",
      icon: <IoShieldCheckmarkOutline size={24} />,
      title: "Wellness & Preventive Care",
      desc: "Packaged, screening-led interventions and medical credentialing for populations and corporate partners.",
      subpillars: [
        "Corporate Wellness Programs",
        "Travel Medicine & International Health Certificates",
        "Health Education Promotion Center"
      ],
      target: 'wellness'
    },
    {
      id: "extended",
      icon: <IoCellularOutline size={24} />,
      title: "Digital & Extended Services",
      desc: "Flexible, technology-driven continuity services extending beyond the traditional clinical floor.",
      subpillars: [
        "Telemedicine / Remote Consultation",
        "Home Sample Collection Service",
        "Medical Records & Health Certificate Services"
      ],
      target: 'telehealth'
    }
  ];

  return (
    <>
      <style>{`
        :root {
          --navy: #0f172a;
          --medical-teal: #14b8a6;
          --slate-50: #f8fafc;
          --slate-100: #f1f5f9;
          --slate-200: #e2e8f0;
          --slate-500: #64748b;
          --slate-600: #475569;
          --white: #ffffff;
          --font-sans: 'Inter', -apple-system, sans-serif;
        }

        .services-section {
          font-family: var(--font-sans);
          position: relative;
          padding: 100px 0;
          background-color: var(--slate-50);
          background-image: 
            radial-gradient(at 0% 0%, rgba(20, 184, 166, 0.03) 0px, transparent 50%),
            radial-gradient(at 100% 100%, rgba(15, 23, 42, 0.02) 0px, transparent 50%);
          overflow: hidden;
        }

        .services-container {
          max-width: 1300px;
          margin: 0 auto;
          padding: 0 2rem;
          position: relative;
          z-index: 1;
        }

        .services-header-flex {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: 5rem;
          gap: 4rem;
        }

        .services-heading { 
          font-size: clamp(2.25rem, 5vw, 3.25rem); 
          color: var(--navy); 
          font-weight: 800; 
          letter-spacing: -0.04em; 
          margin-bottom: 1.5rem; 
        }

        .services-subheading { 
          font-size: 1.15rem; 
          color: var(--slate-600); 
          line-height: 1.7; 
          border-left: 4px solid var(--medical-teal); 
          padding-left: 1.5rem; 
          max-width: 720px; 
        }

        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
          gap: 2rem;
        }

        .service-card {
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(10px);
          padding: 2.5rem;
          text-align: left;
          border: 1px solid rgba(226, 232, 240, 0.7);
          border-radius: 16px;
          cursor: pointer;
          transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
          box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.03);
          width: 100%;
        }

        .service-card:hover {
          background: var(--white);
          border-color: var(--medical-teal);
          transform: translateY(-4px);
          box-shadow: 0 20px 25px -5px rgba(15, 23, 42, 0.06);
        }

        .service-icon-box {
          width: 52px; 
          height: 52px; 
          background: var(--slate-100); 
          border-radius: 12px;
          display: flex; 
          align-items: center; 
          justify-content: center; 
          color: var(--medical-teal);
          margin-bottom: 1.75rem; 
          transition: all 0.3s ease;
        }

        .service-card:hover .service-icon-box { 
          background: var(--medical-teal); 
          color: var(--white); 
        }

        .service-card-title { 
          font-size: 1.3rem; 
          font-weight: 700; 
          color: var(--navy); 
          margin-bottom: 0.75rem; 
          line-height: 1.3;
        }

        .service-card-desc { 
          font-size: 0.95rem; 
          color: var(--slate-500); 
          line-height: 1.6; 
          margin-bottom: 1.5rem; 
        }

        .subpillar-list { 
          list-style: none; 
          padding: 0; 
          margin: 0 0 2rem 0; 
          border-top: 1px dashed var(--slate-200);
          padding-top: 1.25rem;
        }

        .subpillar-item { 
          font-size: 0.85rem; 
          color: var(--slate-600); 
          font-weight: 500;
          line-height: 1.4;
          margin-bottom: 0.75rem;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .subpillar-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: var(--medical-teal);
          opacity: 0.7;
          flex-shrink: 0;
        }

        .service-action-link { 
          margin-top: auto; 
          display: flex; 
          align-items: center; 
          gap: 6px; 
          font-size: 0.8rem; 
          font-weight: 700; 
          color: var(--medical-teal); 
          text-transform: uppercase; 
          letter-spacing: 0.05em;
          transition: gap 0.2s ease;
        }

        .service-card:hover .service-action-link {
          gap: 10px;
        }

        @media (max-width: 991px) {
          .services-header-flex { flex-direction: column; align-items: stretch; margin-bottom: 3.5rem; }
          .services-grid { grid-template-columns: 1fr 1fr; }
        }

        @media (max-width: 640px) {
          .services-grid { grid-template-columns: 1fr; }
          .services-container { padding: 0 1.25rem; }
          .services-section { padding: 70px 0; }
        }
      `}</style>

      <section className="services-section">
        <div className="services-container">
          
          <div className="services-header-flex">
            <div className="services-intro">
              <h2 className="services-heading">Clinical Services</h2>
              <p className="services-subheading">
                Nanguba Medical Center structures its ecosystem into Seven (7) strategic pillars, delivering 23 distinct clinical, diagnostic, and specialty pathways built for patient-centered medical excellence.
              </p>
            </div>
          </div>

          <div className="services-grid">
            {serviceCategories.map((category) => (
              <button 
                key={category.id} 
                className="service-card" 
                onClick={() => appContext.navTo({ item: "services", subItem: category.target })}
                aria-label={`Explore ${category.title}`}
              >
                <div className="service-icon-box">
                  {category.icon}
                </div>
                
                <h3 className="service-card-title">{category.title}</h3>
                <p className="service-card-desc">{category.desc}</p>
                
                <ul className="subpillar-list">
                  {category.subpillars.map((sub, idx) => (
                    <li key={idx} className="subpillar-item">
                      <span className="subpillar-dot" />
                      {sub}
                    </li>
                  ))}
                </ul>

                <div className="service-action-link">
                  View Framework Details <IoChevronForwardOutline />
                </div>
              </button>
            ))}
          </div>

        </div>
      </section>
    </>
  );
}